import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useQuery } from 'data-fetching-client';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import UIModal from 'UIComponents/dialog/UIModal';
import UIWizard from 'UIComponents/dialog/UIWizard';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import Review from '../../records/Review';
import { GET_USER_INSIGHTS } from '../../queries/userInsightsQuery';
export function BaseReviewWizard({
  dataTestId,
  HeaderComponent,
  StepComponents,
  defaultReview = new Review(),
  disableSubmit = false,
  failed,
  loading,
  onReject,
  onConfirm,
  onChange,
  onStepChange,
  onWizardLoad,
  reviewSteps,
  // TODO deprecate once quality ratings are ungated
  reviewType,
  submitTooltip,
  usePrefill = true
}) {
  useEffect(() => {
    if (onWizardLoad) {
      onWizardLoad();
    }
  }, [onWizardLoad]);
  const [review, setReview] = useState(defaultReview);
  const [step, setStep] = useState(reviewSteps[0]);
  const [checkedLegalDisclaimer, setCheckedLegalDisclaimer] = useState(false);
  const {
    data
  } = useQuery(GET_USER_INSIGHTS, {
    skip: !usePrefill
  });
  useEffect(() => {
    if (data) {
      const {
        employeeSize,
        industry
      } = data.userInsights.portalInsights;
      let updatedReview = review;

      if (!review.employees && employeeSize) {
        updatedReview = updatedReview.updateReview({
          employees: employeeSize
        });
      }

      if (!review.marketplaceIndustry && industry) {
        updatedReview = updatedReview.updateReview({
          marketplaceIndustry: industry
        });
      }

      setReview(updatedReview);
    }
  }, [data, review]);
  const onUpdateReview = useCallback(updates => {
    if (Object.prototype.hasOwnProperty.call(updates, 'checkedLegalDisclaimer')) {
      setCheckedLegalDisclaimer(updates.checkedLegalDisclaimer);
    } else {
      const {
        content,
        ratings
      } = updates;

      if (content) {
        updates.content = new Map(Object.entries(content));
      }

      if (ratings) {
        updates.ratings = new Map(Object.entries(ratings));
      }

      const updatedReview = review.updateReview(updates);
      setReview(updatedReview);

      if (onChange) {
        onChange(updatedReview);
      }
    }
  }, [review, onChange]);
  const handleConfirm = useCallback(() => {
    onConfirm(review).then(() => {
      FloatingAlertStore.addAlert({
        message: unescapedText('marketplace-ui-reviews.wizard.alert.submit.message'),
        titleText: unescapedText('marketplace-ui-reviews.wizard.alert.submit.title'),
        type: 'success'
      });
    }).catch(e => {
      let messageKey = 'marketplace-ui-reviews.wizard.alert.error.message';

      try {
        const error = JSON.parse(e.data).subCategory;

        if (isValidI18nKey(`marketplace-ui-reviews.error.${error}`)) {
          messageKey = `marketplace-ui-reviews.error.${error}`;
        }
      } catch (err) {
        Raven.captureMessage('subCategory not provided in backend error response ', {
          level: 'warning',
          extra: {
            error: err
          }
        });
      }

      FloatingAlertStore.addAlert({
        message: unescapedText(messageKey),
        titleText: unescapedText('marketplace-ui-reviews.wizard.alert.error.title'),
        type: 'danger'
      });
    });
  }, [onConfirm, review]);
  return /*#__PURE__*/_jsx(UIModal, {
    "data-test-id": dataTestId,
    use: "fullscreen",
    children: /*#__PURE__*/_jsx(UIWizard, {
      cancellable: true,
      defaultDoneButton: buttonProps => /*#__PURE__*/_jsx(UITooltip, {
        title: submitTooltip,
        disabled: !disableSubmit || !submitTooltip,
        children: /*#__PURE__*/_jsx(UILoadingButton, Object.assign({}, buttonProps, {
          disabled: disableSubmit || buttonProps.disabled,
          loading: loading,
          failed: failed
        }))
      }),
      defaultDoneLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.wizard.submit"
      }),
      disablePrimaryButton: !review.isStepComplete(reviewType, step, checkedLegalDisclaimer),
      headerComponent: HeaderComponent,
      onReject: onReject,
      onConfirm: handleConfirm,
      onStepIndexChange: ({
        target: {
          value
        }
      }) => {
        const nextStep = reviewSteps[value];
        setStep(nextStep);

        if (onStepChange) {
          onStepChange(step, nextStep);
        }
      },
      children: StepComponents.map((Component, index) => /*#__PURE__*/_jsx(Component, {
        name: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.wizard.steps.${reviewSteps[index]}`
        }, index),
        review: review,
        onUpdate: onUpdateReview,
        checkedLegalDisclaimer: checkedLegalDisclaimer
      }, index))
    })
  });
}
export default BaseReviewWizard;